<template>
  <a-modal
    :visible="value"
    title="Edit Header"
    width="700px"
    destroyOnClose
    @cancel="$emit('close')"
  >
    <template slot="default">
      <div class="header-modal">
        <LanguageSelect
          v-model="selectedLanguage"
          :langs="langs"
          :contentFilled="filledLangs"
          :error="formErrorState"
          short-button-label
          @copyFromDefault="handleCopyFromDefault"
          @clear="handleClearLang"
        />
        <a-form-model :model="currentForm" :rules="rules" ref="headerForm">
          <a-form-model-item
            :colon="false"
            label="Image"
            prop="image.url"
            style="max-width: 368px"
          >
            <ImageUploader
              v-model="currentForm.image.url"
              :file-width="1160"
              :file-height="774"
              aspect-ratio="580 / 387"
              has-delete-overlay
              show-edit-button
              @change="handleUpload"
            />
          </a-form-model-item>
          <div>
            <div
              style="
                display: flex;
                align-items: center;
                gap: 16px;
                margin-bottom: 16px;
              "
            >
              <span
                style="
                  font-size: 14px;
                  line-height: 22px;
                  font-weight: 600;
                  color: #262626;
                "
              >
                Header Buttons
              </span>
              <a-button
                type="link"
                size="small"
                style="padding: 0; display: flex; align-items: center"
                @click="addButton"
              >
                <a-icon type="plus" />
                Add
              </a-button>
            </div>
            <DynamicList
              v-model="currentForm.buttons"
              @change="handleListChange"
            >
              <template
                v-slot="{
                  item,
                  index,
                  onMoveUp,
                  onMoveDown,
                  onMoveTop,
                  onMoveBottom,
                  onRemove,
                }"
              >
                <DynamicListItem
                  title="Button"
                  :index="index"
                  :isFirst="index === 0"
                  :isLast="index === currentForm.buttons.length - 1"
                  @moveUp="onMoveUp"
                  @moveDown="onMoveDown"
                  @moveTop="onMoveTop"
                  @moveBottom="onMoveBottom"
                  @remove="onRemove"
                >
                  <div
                    class="d-flex flex-row justify-content-between align-items-start"
                    style="gap: 16px"
                  >
                    <a-form-model-item
                      label="Name"
                      :prop="`buttons.${index}.title`"
                      :colon="false"
                      :validate-status="
                        item.touch.title && !item.title ? 'error' : ''
                      "
                      :help="
                        item.touch.title && !item.title
                          ? 'Please input button name'
                          : ''
                      "
                      required
                      style="width: 100%"
                    >
                      <a-input
                        v-model="item.title"
                        size="large"
                        placeholder="Button title"
                        @blur="item.touch.title = true"
                      />
                    </a-form-model-item>
                    <a-form-model-item
                      label="Deeplink"
                      :prop="`buttons.${index}.deeplink`"
                      :colon="false"
                      :rules="[
                        {
                          required: true,
                          message: 'Please input deeplink',
                          trigger: ['blur'],
                        },
                        { validator: validateDeeplink, trigger: 'blur' },
                      ]"
                      required
                      style="width: 100%"
                    >
                      <a-input
                        v-model="item.deeplink"
                        size="large"
                        placeholder="https://www.olimp.bet"
                        @blur="item.touch.deeplink = true"
                      />
                    </a-form-model-item>
                  </div>
                </DynamicListItem>
              </template>
            </DynamicList>
          </div>
        </a-form-model>
      </div>
    </template>
    <template slot="footer">
      <a-button key="cancel" @click="$emit('close')"> Cancel </a-button>
      <a-button key="save" type="primary" @click="handleSubmit">
        Save
      </a-button>
    </template>
  </a-modal>
</template>
<script>
import DynamicList from '@/components/custom/list/dynamicList.vue'
import DynamicListItem from '@/components/custom/list/dynamicListItem.vue'
import LanguageSelect from '@/components/custom/languageSelect.vue'
import ImageUploader from '@/components/custom/imageUploader.vue'

import { validateURLOrDeeplink } from '@/services/utils/validators'
import apiClient from '@/services/axios'

import { DEFAULT_LANG } from '@/constants/lang'

import { mapState } from 'vuex'
import uuidv1 from 'uuid/v1'
import isEqual from 'lodash/isEqual'

const FORM_FIELDS = {
  image: {
    url: '',
    id: null,
    file: null,
    loading: false,
  },
  buttons: [],
}

export default {
  components: {
    DynamicList,
    DynamicListItem,
    LanguageSelect,
    ImageUploader,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    data: {
      type: Object,
    },
    initLang: {
      type: String,
      default: 'ru',
    },
  },
  data() {
    return {
      form: {
        ru: { ...JSON.parse(JSON.stringify(FORM_FIELDS)) },
      },
      rules: {
        'image.url': [
          {
            required: true,
            message: 'Please upload an image',
            trigger: 'blur',
          },
        ],
      },
      selectedLanguage: 'ru',
      loading: false,
      validationResult: true,
    }
  },
  computed: {
    ...mapState({
      langs: (state) => state.langs.langData,
    }),
    currentForm() {
      return this.form[this.selectedLanguage]
    },
    isInitialForm() {
      return JSON.stringify(this.currentForm) === JSON.stringify(FORM_FIELDS)
    },
    formErrorState() {
      if (this.isInitialForm) {
        return null
      }
      return this.validationResult ? null : 'Form is not filled correctly'
    },
    filledLangs() {
      return this.langs.reduce((obj, { name }) => {
        obj[name] = !isEqual(this.form[name], FORM_FIELDS)
        return obj
      }, {})
    },
  },
  methods: {
    validateDeeplink(_, value, callback) {
      return validateURLOrDeeplink(_, value, callback)
    },
    createFields() {
      return { ...JSON.parse(JSON.stringify(FORM_FIELDS)) }
    },
    initForm() {
      if (this.langs.length === 0) {
        return {
          ru: this.createFields(),
        }
      }

      this.form = this.langs.reduce((acc, lang) => {
        return {
          ...acc,
          [lang.name]: this.createFields(),
        }
      }, {})
    },
    handleSubmit() {
      this.$refs.headerForm.validate((valid) => {
        if (valid) {
          const filteredForm = Object.fromEntries(
            Object.entries(this.form).filter(
              ([key, val]) =>
                JSON.stringify(val) !== JSON.stringify(FORM_FIELDS),
            ),
          )

          this.$emit('submit', filteredForm)
        }
      })
    },
    async handleUpload({ file, url }) {
      const formData = new FormData()
      formData.append('cover', file)

      const resp = await apiClient.post('/admin/custom-page/cover', formData)
      const previewId = resp.data?.data?.id ?? 99

      if (!previewId) {
        const errorMessage =
          resp.data?.error?.message ||
          'Something went wrong while uploading header image'
        this.currentForm.image = {
          ...FORM_FIELDS.image,
        }
        this.$notification.error({
          message: errorMessage,
        })
      } else {
        this.currentForm.image.file = file
        this.currentForm.image.url = url
        this.currentForm.image.id = previewId
      }

      this.$refs.headerForm.validateField('image.url')
    },
    addButton() {
      this.currentForm.buttons.push({
        id: uuidv1(),
        title: '',
        deeplink: '',
        touch: {
          title: false,
          deeplink: false,
        },
      })

      const index = this.currentForm.buttons.length - 1

      this.$set(this.rules, `buttons.${index}.title`, [
        { required: true, message: 'Please enter a title', trigger: 'blur' },
      ])

      this.$set(this.rules, `buttons.${index}.deeplink`, [
        { required: true, message: 'Please enter a deeplink', trigger: 'blur' },
      ])
    },
    handleListChange() {
      const newRules = {}
      this.currentForm.buttons.forEach((btn, index) => {
        newRules[`buttons.${index}.title`] = [
          { required: true, message: 'Please enter a title', trigger: 'blur' },
        ]
        newRules[`buttons.${index}.deeplink`] = [
          {
            required: true,
            message: 'Please enter a deeplink',
            trigger: 'blur',
          },
        ]
      })

      this.rules = {
        'image.url': this.rules['image.url'],
        ...newRules,
      }
    },
    fillForm() {
      if (!this.data) {
        return
      }

      const normalizedData = {}
      Object.entries(this.data).forEach(([key, value]) => {
        normalizedData[key] = {
          ...value,
          buttons: value.buttons?.map((button) => ({
            id: button.id,
            title: button.title,
            deeplink: button.deeplink,
            touch: {
              title: false,
              deeplink: false,
            },
          })),
        }
      })

      this.form = JSON.parse(
        JSON.stringify({ ...this.form, ...normalizedData }),
      )
    },
    handleCopyFromDefault() {
      const defaultForm = this.form[DEFAULT_LANG]

      this.form[this.selectedLanguage] = JSON.parse(
        JSON.stringify(defaultForm),
      )
    },
    validateForm() {
      if (!this.$refs.headerForm) return
      this.$refs.headerForm.validate((valid) => {
        this.validationResult = valid
      })
    },
    handleClearLang(lang) {
      this.form[lang] = { ...FORM_FIELDS }

      this.$refs.headerForm.clearValidate()
    },
  },
  created() {
    this.initForm()
    this.fillForm()

    this.selectedLanguage = this.initLang
  },
  watch: {
    currentForm: {
      deep: true,
      handler() {
        if (this.isInitialForm) {
          return
        }

        this.$nextTick(() => this.validateForm())
      },
    },
    selectedLanguage() {
      if (this.isInitialForm) {
        return
      }

      this.validateForm()
    },
  },
}
</script>
<style lang="scss" scoped>
.header-modal {
  position: relative;
}

.image-uploader {
  margin-top: 18px;
  max-width: 280px;
}
</style>
